import { Validators } from '@angular/forms';
import {
  initVersionedSchema,
  type MixedSchema,
} from '@principle-theorem/editor';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type ILab,
  type ILabJob,
  type ILabJobType,
  type IPatient,
  type IStaffer,
  LabJobStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  type INamedDocument,
  isINamedDocument,
  toNamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';

export interface ILabJobFormData
  extends Omit<
    ILabJob,
    | 'statusHistory'
    | 'dueDate'
    | 'sentAt'
    | 'receivedAt'
    | 'issuedAt'
    | 'completedDate'
    | 'interactions'
    | 'createdAt'
    | 'title'
    | 'updatedAt'
    | 'deleted'
    | 'appointment'
    | 'mentionRefs'
  > {
  appointment?: WithRef<IAppointment>;
  dueDate?: moment.Moment;
}

export class LabJobFormGroup extends TypedFormGroup<ILabJobFormData> {
  constructor() {
    super({
      lab: new TypedFormControl<INamedDocument<ILab>>(
        undefined,
        Validators.required
      ).withGuard(isINamedDocument),
      patient: new TypedFormControl<INamedDocument<IPatient>>(
        undefined,
        Validators.required
      ).withGuard(isINamedDocument),
      practitioner: new TypedFormControl<INamedDocument<IStaffer>>(
        undefined,
        Validators.required
      ),
      appointment: new TypedFormControl<WithRef<IAppointment> | undefined>({
        value: undefined,
        disabled: true,
      }),
      dueDate: new TypedFormControl<moment.Moment>().withGuard(moment.isMoment),
      type: new TypedFormControl<ILabJobType>(undefined, Validators.required),
      cost: new TypedFormControl<number>(0),
      description: new TypedFormControl<MixedSchema>(initVersionedSchema()),
      status: new TypedFormControl<LabJobStatus>(LabJobStatus.Sending),
    });
  }

  override getRawValue(): ILabJobFormData {
    const data: ILabJobFormData = super.getRawValue();
    data.lab = toNamedDocument(data.lab);
    data.patient = toNamedDocument(data.patient);
    data.practitioner = data.practitioner
      ? stafferToNamedDoc(data.practitioner)
      : undefined;
    if (!data.appointment) {
      data.appointment = undefined;
    }
    return data;
  }

  patchFromLabJob(labJob: ILabJobFormData): void {
    this.patchValue(
      {
        lab: labJob.lab,
        patient: labJob.patient,
        practitioner: labJob.practitioner,
        type: labJob.type,
        cost: labJob.cost,
        description: labJob.description || initVersionedSchema(),
        status: labJob.status,
        dueDate: labJob.dueDate,
        appointment: labJob.appointment,
      },
      { emitEvent: false }
    );
    if (!labJob.practitioner) {
      this.controls.practitioner.clearValidators();
      this.controls.practitioner.updateValueAndValidity({ emitEvent: false });
    }
  }
}
